import React from 'react';
import { Box, Chip, Collapse, Stack, Tooltip, Typography, alpha } from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import EventRounded from '@mui/icons-material/EventRounded';

/**
 * One window of time -- a short label plus the concrete detail behind it,
 * shown as two lines on one chip rather than a single run-on sentence, so
 * "what" and "which exact dates/rows" are both visible without reading a
 * paragraph.
 */
export interface ScopeWindow {
  /** Short name, e.g. "This week", "Full call history", "Live snapshot". */
  label: string;
  /** Concrete detail, e.g. "Jul 27 - Aug 2, 2026" or "9,914 leads, created in the last 28 days". */
  detail?: string;
}

export interface ScopeException {
  /** Which section this applies to, e.g. "Weekly trend chart". */
  section: string;
  window: ScopeWindow;
}

/**
 * The one visual chip that means "this is the time window in play here" --
 * used both in the page-level `ScopeBanner` below and, standalone, dropped
 * directly into a section's own `ChartSectionHeader` actions (see e.g.
 * PulsePage's per-section usage). Same look in both places on purpose: see
 * it once at the top, recognize it instantly wherever it shows up again
 * next to an individual chart, instead of having to scroll back up and
 * cross-reference a list to find out if *this* section is the exception.
 */
export function WindowChip({ kind, window }: { kind: 'showing' | 'compare' | 'none'; window: ScopeWindow }) {
  const color = kind === 'compare' ? '#546e7a' : '#2e7d32';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        borderRadius: '6px',
        border: `1px solid ${alpha(kind === 'none' ? '#9e9e9e' : color, 0.35)}`,
        bgcolor: alpha(kind === 'none' ? '#9e9e9e' : color, 0.08),
      }}
    >
      <EventRounded sx={{ fontSize: 14, color: kind === 'none' ? 'text.disabled' : color, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: kind === 'none' ? 'text.secondary' : color, display: 'block', lineHeight: 1.2 }}>
          {window.label}
        </Typography>
        {window.detail && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, fontSize: '0.6875rem' }}>
            {window.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Compact, single-line version of the same chip -- fits inline in a
 * section header's actions row next to an ExplainerToggle/ClockBadge,
 * where the two-line `WindowChip` above is too tall. Full detail moves
 * into the tooltip instead of a second visible line.
 */
export function SectionScopeChip({ window, kind = 'showing' }: { window: ScopeWindow; kind?: 'showing' | 'compare' }) {
  const color = kind === 'compare' ? '#546e7a' : '#2e7d32';
  return (
    <Tooltip title={window.detail ?? window.label} arrow>
      <Chip
        size="small"
        icon={<EventRounded sx={{ fontSize: 14 }} />}
        label={window.label}
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: '0.6875rem',
          height: 22,
          color,
          borderColor: alpha(color, 0.35),
          bgcolor: alpha(color, 0.08),
          '& .MuiChip-icon': { color, ml: '6px' },
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
}

/**
 * The page-level answer to "what time window is this page showing, and
 * what's it compared against" -- now used only where there's a single,
 * genuine page-wide default worth stating once (Model's active-model
 * card). Pulse/Timing/Plan/Queue instead put a `SectionScopeChip` (or two,
 * where a section genuinely mixes two windows) directly in every
 * section's own header via `ChartSectionHeader` -- no page-level banner
 * to scroll back up to and cross-reference, since every section already
 * says what it is on its own.
 *
 * Two chips answer the two questions directly: `showing` (what's on
 * screen right now) and `comparedAgainst` (the baseline it's measured
 * against, or an explicit "no comparison" chip when there isn't one). Any
 * section that covers a *different* window than the page default can
 * still be listed by name underneath via `exceptions`, one row per
 * exception, for the rare case where there's no natural section header to
 * put a chip in directly (see Model's train/validation split).
 */
export default function ScopeBanner({
  showing,
  comparedAgainst,
  exceptions = [],
}: {
  showing: ScopeWindow;
  comparedAgainst?: ScopeWindow | null;
  exceptions?: ScopeException[];
}) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <Box
      sx={(t) => ({
        mb: 2,
        p: 1.25,
        px: 1.5,
        borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.25 }}>Showing</Typography>
        <WindowChip kind="showing" window={showing} />
        <Typography variant="caption" color="text.disabled" sx={{ mx: 0.25 }}>vs.</Typography>
        {comparedAgainst ? (
          <WindowChip kind="compare" window={comparedAgainst} />
        ) : (
          <WindowChip kind="none" window={{ label: 'No comparison', detail: 'a live snapshot, not a before/after' }} />
        )}
        {exceptions.length > 0 && (
          <Chip
            size="small"
            variant="outlined"
            onClick={() => setExpanded((v) => !v)}
            deleteIcon={<ExpandMoreRounded sx={{ fontSize: 16, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />}
            onDelete={() => setExpanded((v) => !v)}
            label={`${exceptions.length} section${exceptions.length === 1 ? '' : 's'} use${exceptions.length === 1 ? 's' : ''} a different window`}
            sx={{ ml: { xs: 0, sm: 'auto' }, fontSize: '0.6875rem', cursor: 'pointer' }}
          />
        )}
      </Stack>
      <Collapse in={expanded}>
        <Stack spacing={0.75} sx={{ mt: 1.25, pt: 1.25, borderTop: (t) => `1px solid ${t.tokens.border.subtle}` }}>
          {exceptions.map((ex) => (
            <Stack key={ex.section} direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 160 }}>{ex.section}</Typography>
              <WindowChip kind="showing" window={ex.window} />
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
